import { useCallback, useEffect, useReducer, useRef } from 'react'

import { ImportApiError } from './importDummyDataHelpers'
import { type ImportStep } from './ImportProgressModal'

export interface ImportStepDef {
  label: string
  run: () => Promise<void>
}

interface State {
  payload: string
  modalOpen: boolean
  steps: ImportStep[]
  overallStatus: 'running' | 'done' | 'failed'
  rateLimited: boolean
  failingIndex: number
}

type Action =
  | { type: 'SET_PAYLOAD', payload: string }
  | { type: 'RUN_START', labels: string[], resumeFromIndex: number }
  | { type: 'STEP_RUNNING', index: number }
  | { type: 'STEP_DONE', index: number }
  | { type: 'FAIL', index: number, message: string, rateLimited: boolean }
  | { type: 'PARSE_FAIL', message: string }
  | { type: 'CLOSE_MODAL' }

const initialState: State = {
  payload: '',
  modalOpen: false,
  steps: [],
  overallStatus: 'running',
  rateLimited: false,
  failingIndex: -1,
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_PAYLOAD':
      return { ...state, payload: action.payload }
    case 'RUN_START':
      // On first run resumeFromIndex=0 (rebuild step list); on retry preserve done steps before resumeFromIndex.
      return {
        ...state,
        modalOpen: true,
        overallStatus: 'running',
        rateLimited: false,
        failingIndex: -1,
        steps: action.labels.map((label, i) => {
          if (i < action.resumeFromIndex) {
            return { label, status: 'done' }
          }
          return { label, status: 'pending' }
        }),
      }
    case 'STEP_RUNNING':
      return {
        ...state,
        steps: state.steps.map((step, i) =>
          i === action.index ? { ...step, status: 'running' } : step,
        ),
      }
    case 'STEP_DONE':
      return {
        ...state,
        steps: state.steps.map((step, i) =>
          i === action.index ? { ...step, status: 'done' } : step,
        ),
        overallStatus: action.index === state.steps.length - 1 ? 'done' : state.overallStatus,
      }
    case 'FAIL':
      return {
        ...state,
        overallStatus: 'failed',
        rateLimited: action.rateLimited,
        failingIndex: action.index,
        steps: state.steps.map((step, i) =>
          i === action.index ? { ...step, status: 'failed', error: action.message } : step,
        ),
      }
    case 'PARSE_FAIL':
      return {
        ...state,
        modalOpen: true,
        overallStatus: 'failed',
        rateLimited: false,
        failingIndex: -1,
        steps: [{ label: 'Submit', status: 'failed', error: action.message }],
      }
    case 'CLOSE_MODAL':
      return { ...state, modalOpen: false }
  }
}

interface UseImportFormArgs {
  title: string
  ready: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInitialData: () => any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateData: (data: any) => boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSteps: (data: any) => ImportStepDef[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencies: any[]
}

export const useImportForm = ({
  title,
  ready,
  getInitialData,
  validateData,
  getSteps,
  dependencies,
}: UseImportFormArgs) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const stepsRef = useRef<ImportStepDef[] | null>(null)

  useEffect(() => {
    if (ready) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const initialData = getInitialData()
      dispatch({ type: 'SET_PAYLOAD', payload: JSON.stringify(initialData, null, 2) })
    }
  // getInitialData intentionally omitted — callers use `dependencies` to declare
  // when to re-seed; including the function reference would re-run on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ...dependencies])

  const runFromIndex = useCallback(async (
    stepDefs: ImportStepDef[],
    fromIndex: number,
  ) => {
    dispatch({
      type: 'RUN_START',
      labels: stepDefs.map(s => s.label),
      resumeFromIndex: fromIndex,
    })

    for (let i = fromIndex; i < stepDefs.length; i++) {
      dispatch({ type: 'STEP_RUNNING', index: i })
      try {
        await stepDefs[i].run()
        dispatch({ type: 'STEP_DONE', index: i })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Import step failed:', error)
        dispatch({
          type: 'FAIL',
          index: i,
          message: error instanceof Error ? error.message : String(error),
          rateLimited: error instanceof ImportApiError && error.status === 429,
        })
        return
      }
    }
  }, [])

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    let parsedPayload: unknown
    try {
      parsedPayload = JSON.parse(state.payload)
      if (!validateData(parsedPayload)) {
        throw new Error(`Invalid ${title.toLowerCase()} payload structure`)
      }
    } catch (error) {
      dispatch({
        type: 'PARSE_FAIL',
        message: error instanceof Error ? error.message : String(error),
      })
      return
    }

    const stepDefs = getSteps(parsedPayload)
    stepsRef.current = stepDefs
    await runFromIndex(stepDefs, 0)
  }, [state.payload, validateData, title, getSteps, runFromIndex])

  const retry = useCallback(() => {
    if (!stepsRef.current || state.failingIndex < 0) return
    void runFromIndex(stepsRef.current, state.failingIndex)
  }, [runFromIndex, state.failingIndex])

  const setPayload = useCallback((payload: string) => {
    dispatch({ type: 'SET_PAYLOAD', payload })
  }, [])

  const closeModal = useCallback(() => dispatch({ type: 'CLOSE_MODAL' }), [])

  const canRetry = state.overallStatus === 'failed' && state.failingIndex >= 0 && stepsRef.current !== null

  return {
    payload: state.payload,
    setPayload,
    handleSubmit,
    modalProps: {
      open: state.modalOpen,
      steps: state.steps,
      overallStatus: state.overallStatus,
      rateLimited: state.rateLimited,
      onClose: closeModal,
      onRetry: canRetry ? retry : undefined,
    },
  }
}
